import React, { FC, ReactNode, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { Image } from '../../components';
import { CameraIcon } from '../../icons';
import { PHOTO_TYPE, STATUS } from '../../resources/enums';
import { ScanModel } from '../../resources/models';
import { ScansService } from '../../services';

export interface IScanPhotoProps {
  className?: string;
  scan?: ScanModel;
  type: PHOTO_TYPE;
  aspectRatio?: number;
  label?: ReactNode;
  labelClass?: string;
  onClick?: () => void;
}

export const ScanPhoto: FC<IScanPhotoProps> = ({ className, scan, type, aspectRatio, label, labelClass, onClick }) => {
  const [photo, setPhoto] = useState<string>();
  const [analyzedPhotoUrl, setAnalyzedPhotoUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scan) {
      setPhoto(undefined);
      setAnalyzedPhotoUrl(undefined);
      return;
    }
    const src = scan[type]?.src;
    if (!src || scan.status !== STATUS.COMPLETED || !scan.points) {
      setPhoto(src);
      setAnalyzedPhotoUrl(undefined);
      return;
    }
  }, [scan, type]);

  useEffect(() => {
    if (!scan?.id) {
      setAnalyzedPhotoUrl(undefined);
      return;
    }

    if (scan.status !== STATUS.COMPLETED || !scan.points) {
      setAnalyzedPhotoUrl(undefined);
      return;
    }

    // Clean up previous object URL
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setIsLoading(true);
    let cancelled = false;

    ScansService.getDrawImage(scan.id, type, false)
      .then((blob) => {
        if (cancelled) {
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setAnalyzedPhotoUrl(objectUrl);
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setAnalyzedPhotoUrl(undefined);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [scan?.id, scan?.points, scan?.status, type]);

  const imageSrc = analyzedPhotoUrl || photo;

  return (
    <div className={className}>
      <div className="relative">
        <Image
          className="h-full w-full object-cover"
          wrapperClass={classNames('rounded-10p', !imageSrc && 'card1', onClick && 'cursor-pointer')}
          src={imageSrc}
          placeholder={<CameraIcon className="text-typo1" size={24} />}
          aspectRatio={aspectRatio}
          spinner
          spinnerSize="sm"
          loading={isLoading}
          onClick={onClick}
        />
      </div>
      {label && <div className={classNames('typo-xs mt-2 text-center text-typo1', labelClass)}>{label}</div>}
    </div>
  );
};
